/**
 * GymMapEditor Component
 *
 * Full-featured polygon editor for defining wall section regions on the floor plan.
 * Used by setters and owners to draw and edit polygon regions.
 *
 * Features:
 * - Full-screen editor layout with sidebar
 * - Floor plan image with pan/zoom
 * - Draw new polygon regions
 * - Edit existing regions (drag vertices)
 * - Delete regions
 * - Undo/redo support
 * - Auto-save with debounce
 * - Works on tablet (iPad)
 *
 * @example
 * ```tsx
 * <GymMapEditor
 *   floorPlan={gym.floorPlan}
 *   onSave={handleSave}
 *   onClose={handleClose}
 * />
 * ```
 */

import { useState, useCallback, useRef, useMemo, useEffect, type CSSProperties } from 'react';
import { Image as KonvaImage, Line, Group } from 'react-konva';
import type Konva from 'konva';
import { MapStage } from './MapStage';
import { WallPolygon } from './WallPolygon';
import { PolygonDrawer } from './PolygonDrawer';
import { VertexHandle } from './VertexHandle';
import { WallSectionSidebar } from './WallSectionSidebar';
import { useFloorPlanImage } from '@/hooks/useFloorPlanImage';
import { useMapEditorState } from '@/hooks/useMapEditorState';
import {
  normalizePoint,
  denormalizePoint,
  denormalizePolygon,
  flattenPolygonPoints,
  type Dimensions,
} from '@/utils/coordinates';
import { snapVertexToAvoidOverlap, snapPointToRegions } from '@/utils/geometry';
import type { GymFloorPlan, Point, WallSectionRegion } from '@flash/shared';

/**
 * Props for GymMapEditor component
 */
export interface GymMapEditorProps {
  /** Floor plan configuration with image and regions */
  floorPlan: GymFloorPlan;
  /** Callback to save updated regions */
  onSave: (regions: WallSectionRegion[]) => Promise<void>;
  /** Callback when editor is closed */
  onClose: () => void;
}

/**
 * Editor toolbar component
 */
const EditorToolbar = ({
  canFinishDrawing,
  isDrawing,
  editingRegionName,
  isSaving,
  hasPendingChanges: _hasPendingChanges,
  onCancel,
  onFinishDrawing,
  onClose,
}: {
  canFinishDrawing: boolean;
  isDrawing: boolean;
  editingRegionName: string | null;
  isSaving: boolean;
  hasPendingChanges: boolean;
  onCancel: () => void;
  onFinishDrawing: () => void;
  onClose: () => void;
}) => (
  <header style={toolbarStyle}>
    <div style={toolbarLeftStyle}>
      <button type="button" onClick={onClose} style={closeButtonStyle} aria-label="Close editor">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19 12H5" />
          <path d="M12 19l-7-7 7-7" />
        </svg>
        <span style={closeButtonTextStyle}>Back</span>
      </button>

      <h1 style={editorTitleStyle}>Edit Floor Plan Regions</h1>
    </div>

    <div style={toolbarCenterStyle}>
      {isSaving && <span style={savingIndicatorStyle}>Saving...</span>}
    </div>

    <div style={toolbarRightStyle}>
      {/* Cancel and Done buttons when drawing or editing */}
      {(isDrawing || editingRegionName) && (
        <>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            style={{
              ...cancelButtonStyle,
              ...(isSaving ? cancelButtonDisabledStyle : {}),
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onFinishDrawing}
            disabled={(isDrawing && !canFinishDrawing) || isSaving}
            style={{
              ...doneButtonStyle,
              ...((isDrawing && !canFinishDrawing) || isSaving ? doneButtonDisabledStyle : {}),
            }}
          >
            Done
          </button>
        </>
      )}
    </div>
  </header>
);

/**
 * GymMapEditor Component
 */
export const GymMapEditor = ({ floorPlan, onSave, onClose }: GymMapEditorProps) => {
  // Load the floor plan image
  const { image, isLoading, hasError, naturalWidth, naturalHeight } = useFloorPlanImage(
    floorPlan.imageUrl
  );

  // Container dimensions (track for responsive sizing)
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number } | null>(
    null
  );

  // Mobile detection and sidebar collapse state
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true); // Start collapsed on mobile

  // Track cursor position for drawing preview
  const [cursorPosition, setCursorPosition] = useState<Point | null>(null);

  // Track vertex position during drag (local state to avoid saves during drag)
  const [draggingVertexPosition, setDraggingVertexPosition] = useState<{
    index: number;
    position: Point;
  } | null>(null);

  // Track if current position causes overlap with other regions
  const [overlapWarning, setOverlapWarning] = useState<string | null>(null);

  // Track pending polygon edits (accumulated changes before hitting "Done")
  const [pendingPolygon, setPendingPolygon] = useState<Point[] | null>(null);

  // Track overlap error when trying to create a region that overlaps another
  const [overlapError, setOverlapError] = useState<string | null>(null);

  // Track if currently snapping to an existing region during drawing
  const [isSnappingToRegion, setIsSnappingToRegion] = useState(false);

  // Use the composed editor state hook
  const editorState = useMapEditorState({
    floorPlan,
    onSave,
  });

  // Use stored dimensions if available, otherwise use loaded dimensions
  const imageWidth = floorPlan.imageWidth || naturalWidth;
  const imageHeight = floorPlan.imageHeight || naturalHeight;
  const dimensions: Dimensions = useMemo(
    () => ({ width: imageWidth, height: imageHeight }),
    [imageWidth, imageHeight]
  );

  // Update container size on mount and resize, detect mobile
  // Re-run when isLoading changes because containerRef isn't available during loading
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          setContainerSize({ width: rect.width, height: rect.height });
        }
      }
      // Detect mobile based on window width
      setIsMobile(window.innerWidth < 768);
    };

    updateSize();

    const resizeObserver = new ResizeObserver(updateSize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Also listen for window resize for mobile detection
    window.addEventListener('resize', updateSize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateSize);
    };
  }, [isLoading]);

  // Calculate initial scale to fit image in container
  const initialScale = useMemo(() => {
    if (!containerSize || imageWidth === 0 || imageHeight === 0) return 1;
    const scaleX = containerSize.width / imageWidth;
    const scaleY = containerSize.height / imageHeight;
    return Math.min(scaleX, scaleY) * 0.9; // 90% to leave some margin
  }, [imageWidth, imageHeight, containerSize]);

  // Calculate initial position to center the image
  const initialPosition = useMemo(() => {
    if (!containerSize || imageWidth === 0 || imageHeight === 0) return { x: 0, y: 0 };
    const scaledWidth = imageWidth * initialScale;
    const scaledHeight = imageHeight * initialScale;
    return {
      x: (containerSize.width - scaledWidth) / 2,
      y: (containerSize.height - scaledHeight) / 2,
    };
  }, [imageWidth, imageHeight, containerSize, initialScale]);

  /**
   * Handle stage click for adding points during drawing
   */
  const handleStageClick = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      if (!editorState.isDrawing) return;

      // Get the stage and pointer position
      const stage = e.target.getStage();
      if (!stage) return;

      // Get pointer position in stage coordinates
      const pointerPos = stage.getPointerPosition();
      if (!pointerPos) return;

      // Convert to image coordinates (account for stage transform)
      const transform = stage.getAbsoluteTransform().copy().invert();
      const imagePos = transform.point(pointerPos);

      // Normalize the coordinates
      const normalizedPoint = normalizePoint({ x: imagePos.x, y: imagePos.y }, dimensions);

      // Snap to existing region vertices/edges
      const { point: snappedPoint } = snapPointToRegions(normalizedPoint, editorState.regions);

      // Check if clicking near the first point to close the polygon
      if (editorState.currentPolygon.length >= 3) {
        const firstPoint = editorState.currentPolygon[0]!;
        const firstPointPixel = denormalizePoint(firstPoint, dimensions);
        const distance = Math.sqrt(
          Math.pow(imagePos.x - firstPointPixel.x, 2) + Math.pow(imagePos.y - firstPointPixel.y, 2)
        );

        // If within 20 pixels of first point, finish the polygon
        if (distance < 20) {
          editorState.finishDrawing().then((result) => {
            if (!result.success && result.error) {
              setOverlapError(result.error);
            }
          });
          return;
        }
      }

      editorState.addPoint(snappedPoint);
    },
    [editorState, dimensions]
  );

  /**
   * Handle stage double-click to finish drawing
   */
  const handleStageDoubleClick = useCallback(
    async (_e: Konva.KonvaEventObject<MouseEvent>) => {
      if (editorState.isDrawing && editorState.canFinishDrawing) {
        const result = await editorState.finishDrawing();
        if (!result.success && result.error) {
          setOverlapError(result.error);
        }
      }
    },
    [editorState]
  );

  /**
   * Handle mouse move for drawing preview
   */
  const handleStageMouseMove = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      if (!editorState.isDrawing) {
        setCursorPosition(null);
        setIsSnappingToRegion(false);
        return;
      }

      const stage = e.target.getStage();
      if (!stage) return;

      const pointerPos = stage.getPointerPosition();
      if (!pointerPos) return;

      const transform = stage.getAbsoluteTransform().copy().invert();
      const imagePos = transform.point(pointerPos);

      const normalizedPoint = normalizePoint({ x: imagePos.x, y: imagePos.y }, dimensions);

      // Snap to existing region vertices/edges for preview
      const { point: snappedPoint, snapped } = snapPointToRegions(
        normalizedPoint,
        editorState.regions
      );

      setCursorPosition(snappedPoint);
      setIsSnappingToRegion(snapped);
    },
    [editorState.isDrawing, editorState.regions, dimensions]
  );

  /**
   * Handle vertex drag start - track which vertex is being dragged
   */
  const handleVertexDragStart = useCallback(
    (vertexIndex: number) => {
      editorState.startDraggingVertex(vertexIndex);
    },
    [editorState]
  );

  /**
   * Get the current working polygon (pending changes or original)
   */
  const getWorkingPolygon = useCallback(() => {
    if (pendingPolygon) return pendingPolygon;
    return editorState.editingRegion?.polygon ?? [];
  }, [pendingPolygon, editorState.editingRegion]);

  /**
   * Handle vertex drag for editing - update local position only (no save)
   * Automatically snaps to avoid overlaps with other regions
   */
  const handleVertexDragMove = useCallback(
    (vertexIndex: number, position: { x: number; y: number }) => {
      const normalizedPoint = normalizePoint(position, dimensions);
      const workingPolygon = getWorkingPolygon();

      // Check for overlaps and snap if needed
      if (workingPolygon.length > 0) {
        const { position: snappedPosition, wasSnapped } = snapVertexToAvoidOverlap(
          normalizedPoint,
          vertexIndex,
          workingPolygon,
          editorState.regions,
          editorState.editingRegionName ?? undefined
        );

        // Update local state with snapped position
        setDraggingVertexPosition({ index: vertexIndex, position: snappedPosition });
        setOverlapWarning(wasSnapped ? 'snapped' : null);
      } else {
        setDraggingVertexPosition({ index: vertexIndex, position: normalizedPoint });
        setOverlapWarning(null);
      }
    },
    [dimensions, getWorkingPolygon, editorState.regions, editorState.editingRegionName]
  );

  /**
   * Handle vertex drag end - update pending polygon (no save until Done)
   */
  const handleVertexDragEnd = useCallback(
    (vertexIndex: number) => {
      // Get the final position from local state (already snapped)
      if (draggingVertexPosition && draggingVertexPosition.index === vertexIndex) {
        // Update pending polygon with the new vertex position
        const workingPolygon = getWorkingPolygon();
        const updatedPolygon = workingPolygon.map((point, index) =>
          index === vertexIndex ? draggingVertexPosition.position : point
        );
        setPendingPolygon(updatedPolygon);
      }
      // Clear drag state
      setDraggingVertexPosition(null);
      setOverlapWarning(null);
      editorState.stopDraggingVertex();
    },
    [draggingVertexPosition, getWorkingPolygon, editorState]
  );

  /**
   * Handle cancel operation - discard pending changes
   */
  const handleCancel = useCallback(() => {
    if (editorState.isDrawing) {
      editorState.cancelDrawing();
    } else if (editorState.editingRegionName) {
      editorState.clearEditing();
    }
    // Clear pending changes
    setPendingPolygon(null);
    setCursorPosition(null);
  }, [editorState]);

  /**
   * Handle finish drawing/editing - save pending changes
   */
  const handleFinish = useCallback(async () => {
    if (editorState.isDrawing) {
      const result = await editorState.finishDrawing();
      if (!result.success && result.error) {
        setOverlapError(result.error);
        return; // Don't clear state, drawing was cancelled
      }
    } else if (editorState.editingRegionName) {
      // Save pending polygon changes if any
      if (pendingPolygon) {
        await editorState.updatePolygon(pendingPolygon);
      }
      editorState.clearEditing();
    }
    // Clear pending changes
    setPendingPolygon(null);
    setCursorPosition(null);
  }, [editorState, pendingPolygon]);

  /**
   * Keyboard shortcuts
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Undo: Ctrl+Z or Cmd+Z
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (editorState.canUndo) {
          editorState.undo();
        }
      }

      // Redo: Ctrl+Y or Ctrl+Shift+Z or Cmd+Shift+Z
      if (
        ((e.ctrlKey || e.metaKey) && e.key === 'y') ||
        ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z')
      ) {
        e.preventDefault();
        if (editorState.canRedo) {
          editorState.redo();
        }
      }

      // Escape: Cancel current operation
      if (e.key === 'Escape') {
        handleCancel();
      }

      // Enter: Finish drawing/editing
      if (e.key === 'Enter') {
        handleFinish();
      }

      // Backspace/Delete: Remove last point when drawing
      if ((e.key === 'Backspace' || e.key === 'Delete') && editorState.isDrawing) {
        e.preventDefault();
        editorState.removeLastPoint();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editorState, handleCancel, handleFinish]);

  // Convert regions to pixel coordinates for rendering
  // Must be before early returns to maintain hooks order
  const regionsWithPixels = useMemo(
    () =>
      editorState.regions.map((region) => ({
        ...region,
        pixelPoints: flattenPolygonPoints(denormalizePolygon(region.polygon, dimensions)),
      })),
    [editorState.regions, dimensions]
  );

  // Get editing region pixel points (apply pending changes and drag position)
  // Must be before early returns to maintain hooks order
  const editingRegionPixels = useMemo(() => {
    if (!editorState.editingRegion) return null;

    // Use pending polygon if available, otherwise use original
    let polygon = pendingPolygon ?? editorState.editingRegion.polygon;

    // Apply current drag position if active
    if (draggingVertexPosition) {
      polygon = polygon.map((point, index) =>
        index === draggingVertexPosition.index ? draggingVertexPosition.position : point
      );
    }

    return denormalizePolygon(polygon, dimensions);
  }, [editorState.editingRegion, pendingPolygon, draggingVertexPosition, dimensions]);

  // Loading state
  if (isLoading) {
    return (
      <div style={editorContainerStyle}>
        <EditorToolbar
          canFinishDrawing={false}
          isDrawing={false}
          editingRegionName={null}
          isSaving={false}
          hasPendingChanges={false}
          onCancel={() => {}}
          onFinishDrawing={() => {}}
          onClose={onClose}
        />
        <div style={loadingContainerStyle}>
          <p>Loading floor plan...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (hasError) {
    return (
      <div style={editorContainerStyle}>
        <EditorToolbar
          canFinishDrawing={false}
          isDrawing={false}
          editingRegionName={null}
          isSaving={false}
          hasPendingChanges={false}
          onCancel={() => {}}
          onFinishDrawing={() => {}}
          onClose={onClose}
        />
        <div style={errorContainerStyle}>
          <p>Failed to load floor plan image</p>
          <button onClick={onClose} style={closeButtonPrimaryStyle}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={editorContainerStyle}>
      {/* Toolbar */}
      <EditorToolbar
        canFinishDrawing={editorState.canFinishDrawing}
        isDrawing={editorState.isDrawing}
        editingRegionName={editorState.editingRegionName}
        isSaving={editorState.isSaving}
        hasPendingChanges={pendingPolygon !== null}
        onCancel={handleCancel}
        onFinishDrawing={handleFinish}
        onClose={onClose}
      />

      {/* Main content area */}
      <div style={mainContentStyle}>
        {/* Sidebar */}
        <WallSectionSidebar
          regions={editorState.regions}
          selectedWallSection={editorState.selectedWallSection}
          editingRegionName={editorState.editingRegionName}
          isDrawing={editorState.isDrawing}
          onSelectForDrawing={(name) => {
            // Prevent starting to draw while editing
            if (editorState.editingRegionName) return;
            editorState.startDrawing(name);
            if (isMobile) setSidebarCollapsed(true); // Collapse sidebar when drawing starts
          }}
          onSelectForEditing={(name) => {
            // Prevent editing while drawing or already editing another region
            if (editorState.isDrawing || editorState.editingRegionName) return;
            editorState.selectRegionForEditing(name);
            if (isMobile) setSidebarCollapsed(true); // Collapse sidebar when editing starts
          }}
          onDeleteRegion={editorState.deleteRegion}
          onRenameRegion={editorState.renameRegion}
          onCancel={handleCancel}
          isSaving={editorState.isSaving}
          isMobile={isMobile}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />

        {/* Map canvas */}
        <div ref={containerRef} style={canvasContainerStyle}>
          {containerSize ? (
            <MapStage
              key={`${isMobile}-${containerSize.width}-${containerSize.height}`}
              width={containerSize.width}
              height={containerSize.height}
              imageWidth={imageWidth}
              imageHeight={imageHeight}
              initialViewport={{
                x: initialPosition.x,
                y: initialPosition.y,
                scale: initialScale,
              }}
              minScale={initialScale * 0.5}
              maxScale={4}
              onClick={handleStageClick}
              onTap={handleStageClick}
              onMouseMove={handleStageMouseMove}
              onDblClick={handleStageDoubleClick}
              draggable={!editorState.isDrawing}
            >
              {/* Floor plan image */}
              {image && (
                <KonvaImage
                  image={image}
                  width={imageWidth}
                  height={imageHeight}
                  listening={false}
                />
              )}

              {/* Existing regions (not being edited) */}
              {regionsWithPixels
                .filter((r) => r.wallSectionName !== editorState.editingRegionName)
                .map((region) => (
                  <WallPolygon
                    key={region.wallSectionName}
                    id={`region-${region.wallSectionName}`}
                    points={region.pixelPoints}
                    label={region.wallSectionName}
                    isSelected={false}
                    onClick={() => {
                      // Prevent editing while drawing or already editing another region
                      if (editorState.isDrawing || editorState.editingRegionName) return;
                      editorState.selectRegionForEditing(region.wallSectionName);
                    }}
                    onTap={() => {
                      // Prevent editing while drawing or already editing another region
                      if (editorState.isDrawing || editorState.editingRegionName) return;
                      editorState.selectRegionForEditing(region.wallSectionName);
                    }}
                  />
                ))}

              {/* Editing region with vertex handles */}
              {editorState.editingRegion && editingRegionPixels && (
                <Group>
                  {/* Polygon fill and stroke - amber when snapping to avoid overlap */}
                  <Line
                    points={flattenPolygonPoints(editingRegionPixels)}
                    closed
                    fill={overlapWarning ? 'rgba(245, 158, 11, 0.3)' : 'rgba(59, 130, 246, 0.3)'}
                    stroke={overlapWarning ? '#f59e0b' : '#3b82f6'}
                    strokeWidth={2}
                    listening={false}
                  />

                  {/* Vertex handles */}
                  {editingRegionPixels.map((point, index) => (
                    <VertexHandle
                      key={index}
                      x={point.x}
                      y={point.y}
                      index={index}
                      isActive={editorState.activeVertexIndex === index}
                      onDragStart={handleVertexDragStart}
                      onDragMove={handleVertexDragMove}
                      onDragEnd={handleVertexDragEnd}
                      radius={10}
                    />
                  ))}
                </Group>
              )}

              {/* Drawing preview */}
              {editorState.isDrawing && (
                <PolygonDrawer
                  points={editorState.currentPolygon}
                  imageWidth={imageWidth}
                  imageHeight={imageHeight}
                  isComplete={editorState.canFinishDrawing}
                  cursorPosition={cursorPosition}
                  isSnapping={isSnappingToRegion}
                />
              )}
            </MapStage>
          ) : (
            <div style={loadingContainerStyle}>
              <p>Initializing canvas...</p>
            </div>
          )}
        </div>
      </div>

      {/* Snap indicator notification (vertex editing) */}
      {overlapWarning && (
        <div style={snapIndicatorStyle}>
          <span>Snapping to boundary</span>
        </div>
      )}

      {/* Snap indicator notification (drawing) */}
      {isSnappingToRegion && editorState.isDrawing && (
        <div style={snapIndicatorStyle}>
          <span>Snapping to region edge</span>
        </div>
      )}

      {/* Save error notification */}
      {editorState.saveError && (
        <div style={errorNotificationStyle}>
          <span>Error saving: {editorState.saveError}</span>
          <button
            type="button"
            onClick={editorState.clearSaveError}
            style={errorDismissButtonStyle}
            aria-label="Dismiss"
          >
            &times;
          </button>
        </div>
      )}

      {/* Overlap error notification */}
      {overlapError && (
        <div style={errorNotificationStyle}>
          <span>{overlapError}</span>
          <button
            type="button"
            onClick={() => setOverlapError(null)}
            style={errorDismissButtonStyle}
            aria-label="Dismiss"
          >
            &times;
          </button>
        </div>
      )}
    </div>
  );
};

// Styles
const editorContainerStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'var(--color-bg-primary, #ffffff)',
  zIndex: 1000,
};

const toolbarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '56px',
  minHeight: '56px',
  padding: '0 16px',
  backgroundColor: 'var(--color-bg-primary, #ffffff)',
  borderBottom: '1px solid var(--color-border, #e5e7eb)',
};

const toolbarLeftStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
};

const toolbarCenterStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
};

const toolbarRightStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const closeButtonStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 12px',
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary, #6b7280)',
  cursor: 'pointer',
};

const closeButtonTextStyle: CSSProperties = {
  display: 'inline',
};

const editorTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: '1rem',
  fontWeight: 600,
  color: 'var(--color-text-primary, #1f2937)',
};

const savingIndicatorStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary, #6b7280)',
};

const doneButtonStyle: CSSProperties = {
  padding: '8px 16px',
  backgroundColor: 'var(--color-primary, #3b82f6)',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'white',
  cursor: 'pointer',
};

const doneButtonDisabledStyle: CSSProperties = {
  backgroundColor: 'var(--color-text-secondary, #9ca3af)',
  cursor: 'not-allowed',
};

const cancelButtonStyle: CSSProperties = {
  padding: '8px 16px',
  backgroundColor: '#ef4444',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'white',
  cursor: 'pointer',
};

const cancelButtonDisabledStyle: CSSProperties = {
  opacity: 0.5,
  cursor: 'not-allowed',
};

const mainContentStyle: CSSProperties = {
  display: 'flex',
  flex: 1,
  overflow: 'hidden',
};

const canvasContainerStyle: CSSProperties = {
  flex: 1,
  backgroundColor: 'var(--color-bg-secondary, #f3f4f6)',
  overflow: 'hidden',
};

const loadingContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  fontSize: '1rem',
  color: 'var(--color-text-secondary, #6b7280)',
};

const errorContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  gap: '16px',
  fontSize: '1rem',
  color: 'var(--color-error, #ef4444)',
};

const closeButtonPrimaryStyle: CSSProperties = {
  padding: '10px 20px',
  backgroundColor: 'var(--color-primary, #3b82f6)',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'white',
  cursor: 'pointer',
};

const snapIndicatorStyle: CSSProperties = {
  position: 'fixed',
  bottom: '16px',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
  padding: '10px 16px',
  backgroundColor: 'rgba(245, 158, 11, 0.95)',
  borderRadius: '8px',
  color: 'white',
  fontSize: '0.875rem',
  fontWeight: 500,
  zIndex: 1001,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
};

const errorNotificationStyle: CSSProperties = {
  position: 'fixed',
  bottom: '16px',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 16px',
  backgroundColor: 'var(--color-error-bg, #fef2f2)',
  border: '1px solid var(--color-error-border, #fecaca)',
  borderRadius: '8px',
  color: 'var(--color-error, #991b1b)',
  fontSize: '0.875rem',
  zIndex: 1001,
};

const errorDismissButtonStyle: CSSProperties = {
  padding: '4px 8px',
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: '4px',
  fontSize: '1.25rem',
  color: 'inherit',
  cursor: 'pointer',
};

export default GymMapEditor;
